/**
 * Os feedbacks dos clientes.
 *
 * Nas filas (cartões, prints) as setas andam uma largura para o lado. No
 * destaque mostra-se um de cada vez, com pontos, e passa sozinho a cada 7 s —
 * para quando o rato ou o foco lá estão, e não passa com o movimento
 * reduzido. Um print abre em grande numa janela.
 */
const PASSA = 7000;

class ClaroFeedbacks extends HTMLElement {
  #atual = 0;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #relogio;

  connectedCallback() {
    this.lista = this.querySelector('[data-claro-fb-lista]');
    this.caixa = this.querySelector('[data-claro-fb-caixa]');
    this.grande = this.querySelector('[data-claro-fb-grande]');
    this.parado = matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.controlo = new AbortController();
    const { signal } = this.controlo;

    this.addEventListener('click', (evento) => this.#clique(evento), { signal });
    if (this.dataset.estilo === 'destaque') this.#montaDestaque(signal);
    if (this.dataset.estilo === 'avaliacoes') this.#montaFila(signal);
  }

  /* ── avaliações: as bolinhas seguem a fila, e levam ao cartão ── */
  #itensFila() {
    return [...this.querySelectorAll('.claro-fb__item')].filter((item) => item.offsetParent !== null);
  }

  /** @param {AbortSignal} signal */
  #montaFila(signal) {
    const pontos = this.querySelector('[data-claro-fb-pontos]');
    const lista = this.lista;
    if (!pontos || !lista) return;

    // Uma bolinha por posição da fila, não por cartão: no computador cabem
    // três ou quatro de cada vez, e a última posição já os mostra a todos.
    const desenha = () => {
      const itens = this.#itensFila();
      const passo = itens.length > 1 ? itens[1].offsetLeft - itens[0].offsetLeft : 1;
      const cabem = Math.max(1, Math.floor((lista.clientWidth + 8) / passo));
      const posicoes = Math.max(0, itens.length - cabem + 1);
      if (pontos.childElementCount !== (posicoes > 1 ? posicoes : 0)) {
        pontos.replaceChildren(
          ...(posicoes > 1 ? Array.from({ length: posicoes }, (_, i) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'claro-fb__ponto';
            b.setAttribute('data-claro-fb-vai', String(i));
            b.setAttribute('aria-label', `Avaliações a partir da ${i + 1}`);
            return b;
          }) : [])
        );
      }
      marca();
    };

    const marca = () => {
      const itens = this.#itensFila();
      const n = pontos.childElementCount;
      if (!n) return;
      const passo = itens.length > 1 ? itens[1].offsetLeft - itens[0].offsetLeft : 1;
      const fim = lista.scrollLeft + lista.clientWidth >= lista.scrollWidth - 4;
      const atual = fim ? n - 1 : Math.min(n - 1, Math.round(lista.scrollLeft / passo));
      pontos.querySelectorAll('[data-claro-fb-vai]').forEach((p, k) => p.setAttribute('aria-current', String(k === atual)));
    };

    lista.addEventListener('scroll', marca, { signal, passive: true });
    addEventListener('resize', desenha, { signal, passive: true });
    desenha();
  }

  /** @param {MouseEvent} evento */
  #clique(evento) {
    const alvo = evento.target;
    if (!(alvo instanceof Element)) return;

    const seta = alvo.closest('[data-claro-fb-anda]');
    if (seta) return this.#anda(Number(seta.getAttribute('data-claro-fb-anda')));

    const ponto = alvo.closest('[data-claro-fb-ponto]');
    if (ponto) return this.#mostra(Number(ponto.getAttribute('data-claro-fb-ponto')));

    const vai = alvo.closest('[data-claro-fb-vai]');
    if (vai && this.lista) {
      const item = this.#itensFila()[Number(vai.getAttribute('data-claro-fb-vai'))];
      if (item) this.lista.scrollTo({ left: item.offsetLeft - this.lista.offsetLeft, behavior: this.parado ? 'auto' : 'smooth' });
      return;
    }

    const print = alvo.closest('[data-claro-fb-abre]');
    if (print) return this.#abre(print);

    if (alvo === this.caixa || alvo.closest('[data-claro-fb-fecha]')) {
      if (this.caixa instanceof HTMLDialogElement) this.caixa.close();
    }
  }

  /** @param {number} sentido */
  #anda(sentido) {
    if (this.dataset.estilo === 'destaque') return this.#mostra(this.#atual + sentido);
    this.lista?.scrollBy({ left: sentido * this.lista.clientWidth * 0.85, behavior: this.parado ? 'auto' : 'smooth' });
  }

  /* ── destaque ── */
  #itensTexto() {
    return [...this.querySelectorAll('.claro-fb__item')].filter((item) => item.querySelector('.claro-fb__corpo'));
  }

  /** @param {AbortSignal} signal */
  #montaDestaque(signal) {
    const itens = this.#itensTexto();
    const pontos = this.querySelector('[data-claro-fb-pontos]');
    if (pontos && itens.length > 1) {
      pontos.replaceChildren(
        ...itens.map((_, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'claro-fb__ponto';
          b.setAttribute('data-claro-fb-ponto', String(i));
          b.setAttribute('aria-label', `Feedback ${i + 1} de ${itens.length}`);
          return b;
        })
      );
    }
    this.#mostra(0);

    if (this.parado || itens.length < 2) return;
    const liga = () => { clearInterval(this.#relogio); this.#relogio = setInterval(() => this.#mostra(this.#atual + 1), PASSA); };
    const para = () => clearInterval(this.#relogio);
    this.addEventListener('mouseenter', para, { signal });
    this.addEventListener('mouseleave', liga, { signal });
    this.addEventListener('focusin', para, { signal });
    this.addEventListener('focusout', liga, { signal });
    liga();
  }

  /** @param {number} i */
  #mostra(i) {
    const itens = this.#itensTexto();
    if (!itens.length) return;
    this.#atual = (i + itens.length) % itens.length;
    itens.forEach((item, k) => item.toggleAttribute('data-ativo', k === this.#atual));
    this.querySelectorAll('[data-claro-fb-ponto]').forEach((p, k) => p.setAttribute('aria-current', String(k === this.#atual)));
  }

  /* ── o print em grande ── */
  /** @param {Element} botao */
  #abre(botao) {
    const img = botao.querySelector('img');
    if (!img || !(this.caixa instanceof HTMLDialogElement) || !(this.grande instanceof HTMLImageElement)) return;
    this.grande.src = img.currentSrc || img.src;
    this.grande.alt = img.alt;
    this.caixa.showModal();
  }
}

if (!customElements.get('claro-feedbacks')) customElements.define('claro-feedbacks', ClaroFeedbacks);
